import { GitBranch } from 'lucide-react';
import { forwardRef } from 'react';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { DevOrigin } from 'v5/platform/dev-origin/devOrigin.protocol';
import { cn } from 'v5/platform/dom/cn';

export interface DevOriginBadgeProps
  extends React.ComponentPropsWithoutRef<'div'> {
  origin: DevOrigin;
}

/** Branch and folder a local dev server runs from, with the full path in the tooltip. */
export const DevOriginBadge = forwardRef<HTMLDivElement, DevOriginBadgeProps>(
  ({ origin, className, ...rest }, ref) => {
    const folder = origin.directory.split(/[\\/]/).at(-1) || origin.directory;
    const branch = origin.branch ?? 'no git';

    return (
      <SimpleTooltip
        side="right"
        align="end"
        delayDuration={300}
        title={
          <div className="space-y-0.5 font-mono text-xs">
            <div>{branch}</div>
            <div className="opacity-70">{origin.directory}</div>
          </div>
        }
      >
        <div
          ref={ref}
          role="note"
          tabIndex={0}
          aria-label={`Dev server: ${branch} in ${origin.directory}`}
          className={cn(
            'flex items-center gap-2 rounded-md border border-dashed border-border-default px-3 py-1.5 text-xs text-fg-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
          {...rest}
        >
          <GitBranch className="size-3.5 shrink-0" />
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate font-medium text-fg-default">{branch}</div>
            <div className="truncate">{folder}</div>
          </div>
        </div>
      </SimpleTooltip>
    );
  }
);

DevOriginBadge.displayName = 'DevOriginBadge';
